import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Dropdown } from 'react-bootstrap';
import { CalendarClock, CheckCircle, Filter, FolderKanban, Plus, Search, X } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { useProjects } from '../../hooks/projects/useProjects';
import { useProjectDelmoment } from '../../hooks/projects/useProjectDelmoment';
import { useUsersData, Userprofile, toExternalUsers } from '../../Helpers/UsersContext';
import { useBuildingsData } from '../../Helpers/BuildingSummariesContext';
import { useBuildingTagFilter } from '../../hooks/useBuildingTagFilter';
import { useBuildingKeyFilter } from '../../hooks/useBuildingKeyFilter';
import { UNTAGGED_OPTION_VALUE } from '../../hooks/mapOverview/buildingSort';
import { loadFromInstanceStorage, saveToInstanceStorage } from '../../Helpers/instanceStorage';
import { formatDateForStorage } from '../../Helpers/DateUtils';
import { BuildingType } from '../../types/BuildingTypeEnums';
import { translate } from '../../types/EnumTranslations';
import { ProjectWithBuilding } from '../../api/projectsApi';
import { workOrdersApi } from '../../api/workOrdersApi';
import { WorkOrderWithBuilding, getAssignees, getDelegations } from '../../types/workOrders';
import { AssignableRecord } from '../../types/assignees';
import {
    DelmomentSummary,
    datePart,
    summarizeDelmoment,
    isUrgentProject,
    sortInProgressProjects,
    sortPlannedProjects,
    sortCompletedProjects,
    filterCompletedByYear,
    groupByBuilding,
    GROUP_EXPANDED_BELOW
} from '../../Helpers/projectBoard';
import ColumnCard from '../Förvaltning/components/ColumnCard';
import FilterCheckboxColumn from '../Förvaltning/components/FilterCheckboxColumn';
import WorkOrderDetailModal from '../Förvaltning/components/WorkOrderDetailModal';
import CreateWorkOrderModal from '../Förvaltning/components/CreateWorkOrderModal';
import AssignWorkOrderModal from '../../Components/AssignWorkOrderModal';
import DueDateModal from '../Förvaltning/components/DueDateModal';
import { UnifiedTask, workOrderToTask } from '../Förvaltning/components/TaskCard';
import ProjectBoardCard, { projectDetailPath } from './components/ProjectBoardCard';
import DelmomentList from './components/DelmomentList';
import BuildingGroupCard from './components/BuildingGroupCard';
import LinkWorkOrdersModal from './components/LinkWorkOrdersModal';
import NewProjectModal from './components/NewProjectModal';
import '../Förvaltning/ManagementPage.css';
import './ProjectBoardPage.css';

// Instance storage keys for the building filter (separate from Förvaltning's
// so the two boards can be filtered independently)
const STORAGE_KEY_BUILDING_TYPES = 'projectBoard_selectedBuildingTypes';
const STORAGE_KEY_BUILDING_TAGS = 'projectBoard_deselectedBuildingTags';

const ALL_BUILDING_TYPES: BuildingType[] = [
    BuildingType.standard,
    BuildingType.complement,
    BuildingType.toiletArchipelago,
    BuildingType.toiletReserve,
    BuildingType.other,
];

function loadBuildingTypesFromStorage(): BuildingType[] {
    const stored = loadFromInstanceStorage<BuildingType[]>(STORAGE_KEY_BUILDING_TYPES, ALL_BUILDING_TYPES);
    if (!Array.isArray(stored)) return ALL_BUILDING_TYPES;
    return stored.filter(v => ALL_BUILDING_TYPES.includes(v));
}

type BoardZone = 'planerade' | 'pagaende' | 'utforda';

const EMPTY_SUMMARY: DelmomentSummary = { total: 0, completed: 0, overdue: 0 };

/**
 * ProjectBoardPage - the project dashboard. Three lifecycle columns
 * (Planerade / Pågående / Utförda) of project cards, each expandable to its
 * delmoment (work orders linked to the project). Served at /projects (all
 * buildings) and /building/:id/projects (one building).
 */
const ProjectBoardPage: React.FC = () => {
    const { id: routeBuildingKey } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const isSmallScreen = useIsSmallScreen();
    const [mobileZone, setMobileZone] = useState<BoardZone>('pagaende');

    // Today's date, fixed for the render, drives every due-date wording
    const today = useMemo(() => datePart(formatDateForStorage(new Date())), []);
    const currentYear = new Date().getFullYear();

    // "Visa alla" on the Utförda column lifts the current-year limit
    const [showAllCompleted, setShowAllCompleted] = useState(false);

    // Data
    const {
        planned: plannedProjects,
        active: activeProjects,
        completed: completedProjects,
        loading: projectsLoading,
        refresh: refreshProjects
    } = useProjects(undefined, routeBuildingKey);
    const {
        byProject: delmomentByProject,
        loading: delmomentLoading,
        refresh: refreshDelmoment
    } = useProjectDelmoment();
    const { users, refreshUsers } = useUsersData();
    const { buildings } = useBuildingsData();

    const routeBuilding = useMemo(
        () => (routeBuildingKey ? buildings?.find(b => b.key === routeBuildingKey) : undefined),
        [buildings, routeBuildingKey]
    );

    useDocumentTitle(routeBuilding?.name ? `Projekt - ${routeBuilding.name}` : 'Projekt');

    // The old list page opened a project with ?project=; such links (timeline,
    // documents, component page) now land on the detail route instead.
    const legacyProjectParam = searchParams.get('project');
    const allProjects = useMemo(
        () => [...plannedProjects, ...activeProjects, ...completedProjects],
        [plannedProjects, activeProjects, completedProjects]
    );
    useEffect(() => {
        if (!legacyProjectParam) return;
        if (routeBuildingKey) {
            navigate(`/building/${routeBuildingKey}/projects/${legacyProjectParam}`, { replace: true });
            return;
        }
        if (projectsLoading) return;
        const project = allProjects.find(p => p.key === legacyProjectParam);
        if (project) {
            navigate(projectDetailPath(project), { replace: true });
        } else {
            setSearchParams(prev => {
                const next = new URLSearchParams(prev);
                next.delete('project');
                return next;
            }, { replace: true });
        }
    }, [legacyProjectParam, routeBuildingKey, projectsLoading, allProjects, navigate, setSearchParams]);

    // Current user / admin
    const auth = getAuth();
    const isAdmin = users?.find(u => u.key === auth.currentUser?.uid)?.admin || false;

    // UID -> name
    const userNameMap = useMemo(() => {
        const map = new Map<string, string>();
        users?.forEach((u: Userprofile) => map.set(u.key, u.name || u.key));
        return map;
    }, [users]);
    const getUserName = useCallback((uid: string): string => userNameMap.get(uid) || uid, [userNameMap]);

    const activeUsers = useMemo(() =>
        (users ?? []).filter((u: Userprofile) => u.accessToWeb && !u.deleted && !u.isExternal),
        [users]
    );

    // ---- Building filter (hidden on the building's own board) ----
    const [selectedBuildingTypes, setSelectedBuildingTypes] = useState<BuildingType[]>(loadBuildingTypesFromStorage);
    useEffect(() => {
        saveToInstanceStorage(STORAGE_KEY_BUILDING_TYPES, selectedBuildingTypes);
    }, [selectedBuildingTypes]);

    const buildingTypeMap = useMemo(() => {
        const map = new Map<string, BuildingType>();
        buildings?.forEach(b => {
            if (b.key && b.buildingType) map.set(b.key, b.buildingType);
        });
        return map;
    }, [buildings]);

    const matchesBuildingTypeFilter = useCallback((buildingKey: string): boolean => {
        if (selectedBuildingTypes.length === ALL_BUILDING_TYPES.length) return true;
        if (selectedBuildingTypes.length === 0) return false;
        const type = buildingTypeMap.get(buildingKey);
        if (!type) return true;
        return selectedBuildingTypes.includes(type);
    }, [selectedBuildingTypes, buildingTypeMap]);

    const toggleBuildingType = (type: BuildingType) => {
        setSelectedBuildingTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
    };
    const toggleAllBuildingTypes = () => {
        setSelectedBuildingTypes(prev => prev.length === ALL_BUILDING_TYPES.length ? [] : ALL_BUILDING_TYPES);
    };

    const {
        availableTags,
        tagOptionCount,
        selectedTagOptionCount,
        tagFilterActive,
        allTagOptionsSelected,
        isTagOptionSelected,
        toggleTagOption,
        toggleAllTagOptions,
        matchesBuildingTagFilter,
    } = useBuildingTagFilter(STORAGE_KEY_BUILDING_TAGS);

    // Specific-building filter (session only, see the hook)
    const {
        buildingOptions,
        allBuildingsSelected,
        buildingKeyFilterActive,
        isBuildingSelected,
        toggleBuilding,
        toggleAllBuildings,
        matchesBuildingKeyFilter,
    } = useBuildingKeyFilter(buildings);

    const matchesBuildingFilter = useCallback((buildingKey: string): boolean => {
        if (routeBuildingKey) return true;
        return matchesBuildingTypeFilter(buildingKey)
            && matchesBuildingTagFilter(buildingKey)
            && matchesBuildingKeyFilter(buildingKey);
    }, [routeBuildingKey, matchesBuildingTypeFilter, matchesBuildingTagFilter, matchesBuildingKeyFilter]);

    // The badge counts types + groups only; the building column just colours
    // the button active (see ManagementPage)
    const tagColumnVisible = availableTags.length > 0;
    const buildingColumnVisible = buildingOptions.length > 0;
    const typeFilterActive = selectedBuildingTypes.length < ALL_BUILDING_TYPES.length;
    const buildingFilterActive = typeFilterActive || tagFilterActive || buildingKeyFilterActive;
    const selectedFilterCount = selectedBuildingTypes.length + (tagColumnVisible ? selectedTagOptionCount : 0);
    const totalFilterCount = ALL_BUILDING_TYPES.length + (tagColumnVisible ? tagOptionCount : 0);

    // ---- Search ----
    const [searchQuery, setSearchQuery] = useState('');
    const normalizedSearchQuery = useMemo(() => searchQuery.trim().toLowerCase(), [searchQuery]);
    const matchesSearchQuery = useCallback((...texts: (string | undefined)[]): boolean => {
        if (!normalizedSearchQuery) return true;
        return texts.some(t => t?.toLowerCase().includes(normalizedSearchQuery));
    }, [normalizedSearchQuery]);

    const getAssociatedNames = useCallback((resource: AssignableRecord): string[] => [
        ...Object.keys(getAssignees(resource)).map(getUserName),
        ...Object.keys(getDelegations(resource)).map(getUserName)
    ], [getUserName]);

    // A project matches the search through its own fields or any delmoment's
    const matchesProject = useCallback((project: ProjectWithBuilding): boolean => {
        if (!matchesBuildingFilter(project.buildingKey)) return false;
        if (!normalizedSearchQuery) return true;
        const delmoment = delmomentByProject.get(project.key) ?? [];
        return matchesSearchQuery(
            project.title,
            project.buildingName,
            ...getAssociatedNames(project),
            ...delmoment.map(wo => wo.title),
            ...delmoment.flatMap(wo => getAssociatedNames(wo))
        );
    }, [matchesBuildingFilter, normalizedSearchQuery, delmomentByProject, matchesSearchQuery, getAssociatedNames]);

    // ---- Columns ----
    const summaryFor = useCallback((projectKey: string): DelmomentSummary => {
        const delmoment = delmomentByProject.get(projectKey);
        return delmoment ? summarizeDelmoment(delmoment, today) : EMPTY_SUMMARY;
    }, [delmomentByProject, today]);

    const plannedColumn = useMemo(
        () => sortPlannedProjects(plannedProjects.filter(matchesProject)),
        [plannedProjects, matchesProject]
    );
    const inProgressColumn = useMemo(
        () => sortInProgressProjects(activeProjects.filter(matchesProject), delmomentByProject, today),
        [activeProjects, matchesProject, delmomentByProject, today]
    );
    const completedAll = useMemo(
        () => sortCompletedProjects(completedProjects.filter(matchesProject)),
        [completedProjects, matchesProject]
    );
    const completedColumn = useMemo(
        () => filterCompletedByYear(completedAll, showAllCompleted ? undefined : currentYear),
        [completedAll, showAllCompleted, currentYear]
    );

    // ---- Expand state (session only) ----
    // Seeded once from the urgency rule when both projects and delmoment have
    // loaded; later refreshes keep whatever the user opened or closed.
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());
    const [expandSeeded, setExpandSeeded] = useState(false);
    useEffect(() => {
        if (expandSeeded || projectsLoading || delmomentLoading) return;
        const urgent = activeProjects
            .filter(p => isUrgentProject(p, summaryFor(p.key), today))
            .map(p => p.key);
        setExpandedKeys(new Set(urgent));
        setExpandSeeded(true);
    }, [expandSeeded, projectsLoading, delmomentLoading, activeProjects, summaryFor, today]);

    const toggleExpanded = useCallback((projectKey: string) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(projectKey)) next.delete(projectKey); else next.add(projectKey);
            return next;
        });
    }, []);

    // ---- Building groups (all-buildings board; session only) ----
    // Keyed `${zone}:${buildingKey}`; only the user's own choices are stored,
    // otherwise a group is open when it holds fewer than GROUP_EXPANDED_BELOW.
    const [groupOverrides, setGroupOverrides] = useState<Map<string, boolean>>(() => new Map());
    const isGroupExpanded = useCallback((groupId: string, count: number) =>
        groupOverrides.get(groupId) ?? count < GROUP_EXPANDED_BELOW,
    [groupOverrides]);
    const toggleGroup = useCallback((groupId: string, count: number) => {
        setGroupOverrides(prev => {
            const next = new Map(prev);
            next.set(groupId, !(prev.get(groupId) ?? count < GROUP_EXPANDED_BELOW));
            return next;
        });
    }, []);

    // ---- Delmoment detail modal (URL-driven, as on Förvaltning) ----
    const viewingWorkOrderKey = searchParams.get('wo');
    const openWorkOrder = useCallback((wo: WorkOrderWithBuilding) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('wo', wo.key);
            return next;
        });
    }, [setSearchParams]);
    const closeWorkOrder = useCallback(() => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete('wo');
            return next;
        });
    }, [setSearchParams]);

    const refreshBoard = useCallback(() => {
        refreshProjects();
        refreshDelmoment();
    }, [refreshProjects, refreshDelmoment]);

    // ---- Quick actions ----
    // Key of the delmoment open in the Tilldela modal; resolved live so the
    // modal sees refreshed assignees after each add/remove.
    const [assigningWorkOrderKey, setAssigningWorkOrderKey] = useState<string | null>(null);
    const assigningWorkOrder = useMemo(() => {
        if (!assigningWorkOrderKey) return null;
        for (const list of delmomentByProject.values()) {
            const hit = list.find((wo) => wo.key === assigningWorkOrderKey);
            if (hit) return hit;
        }
        return null;
    }, [assigningWorkOrderKey, delmomentByProject]);
    const externalUserList = useMemo(() => toExternalUsers(users), [users]);
    const [dueDateTask, setDueDateTask] = useState<UnifiedTask | null>(null);

    const handleAssign = useCallback((wo: WorkOrderWithBuilding) => setAssigningWorkOrderKey(wo.key), []);
    const handleSetDueDate = useCallback((wo: WorkOrderWithBuilding) => setDueDateTask(workOrderToTask(wo)), []);

    const handleDueDateConfirm = async (dueDate: string | null) => {
        if (!dueDateTask?.workOrder) return;
        await workOrdersApi.updateDueDate(dueDateTask.workOrder.key, dueDate);
        setDueDateTask(null);
        refreshDelmoment();
    };

    // ---- Create / link delmoment, new project ----
    const [creatingForProject, setCreatingForProject] = useState<ProjectWithBuilding | null>(null);
    const [linkingForProject, setLinkingForProject] = useState<ProjectWithBuilding | null>(null);
    const [showNewProject, setShowNewProject] = useState(false);

    // Loose open work orders of the linking project's building, fetched when
    // the link modal opens (the board hook only holds linked ones)
    const [unlinkedWorkOrders, setUnlinkedWorkOrders] = useState<WorkOrderWithBuilding[]>([]);
    useEffect(() => {
        if (!linkingForProject) {
            setUnlinkedWorkOrders([]);
            return;
        }
        let cancelled = false;
        workOrdersApi.getByBuilding(linkingForProject.buildingKey)
            .then(list => {
                if (cancelled) return;
                setUnlinkedWorkOrders(
                    list
                        .filter(wo => !wo.projectKey)
                        .map(wo => ({ ...wo, buildingName: linkingForProject.buildingName ?? 'Okänd byggnad' }))
                );
            })
            .catch(err => {
                console.error('Failed to load work orders for linking:', err);
                if (!cancelled) setUnlinkedWorkOrders([]);
            });
        return () => { cancelled = true; };
    }, [linkingForProject]);

    const handleLink = async (workOrderKeys: string[]) => {
        if (!linkingForProject) return;
        await Promise.all(workOrderKeys.map(key => workOrdersApi.linkToProject(key, linkingForProject.key)));
        refreshDelmoment();
    };

    const handleProjectCreated = (projectKey: string, buildingKey: string) => {
        setShowNewProject(false);
        navigate(`/building/${buildingKey}/projects/${projectKey}`);
    };

    // ---- Render helpers ----
    const renderCards = (projects: ProjectWithBuilding[]) => projects.map(project => {
        const expanded = expandedKeys.has(project.key);
        return (
            <ProjectBoardCard
                key={project.key}
                project={project}
                summary={summaryFor(project.key)}
                today={today}
                expanded={expanded}
                onToggle={() => toggleExpanded(project.key)}
                getUserName={getUserName}
            >
                <DelmomentList
                    workOrders={delmomentByProject.get(project.key) ?? []}
                    today={today}
                    getUserName={getUserName}
                    onOpen={openWorkOrder}
                    onAssign={isAdmin ? handleAssign : undefined}
                    onSetDueDate={isAdmin ? handleSetDueDate : undefined}
                    onCreate={isAdmin ? () => setCreatingForProject(project) : undefined}
                    onLink={isAdmin ? () => setLinkingForProject(project) : undefined}
                />
            </ProjectBoardCard>
        );
    });

    // On the all-buildings board a column is grouped by building; a building's
    // own board lists its projects directly.
    const renderColumn = (zone: BoardZone, projects: ProjectWithBuilding[]) => {
        if (routeBuildingKey) return renderCards(projects);
        return groupByBuilding(projects).map(group => {
            const first = group.projects[0];
            const groupId = `${zone}:${group.buildingKey}`;
            const count = group.projects.length;
            return (
                <BuildingGroupCard
                    key={groupId}
                    groupId={groupId.replace(':', '-')}
                    buildingName={first.buildingName}
                    buildingType={first.buildingType}
                    coverPhotoPath={first.buildingCoverPhotoPath}
                    groupTags={first.buildingGroupTags}
                    itemCount={count}
                    expanded={isGroupExpanded(groupId, count)}
                    onToggle={() => toggleGroup(groupId, count)}
                >
                    {renderCards(group.projects)}
                </BuildingGroupCard>
            );
        });
    };

    const loading = projectsLoading || delmomentLoading;

    const mobileZones: { key: BoardZone; label: string; count: number }[] = [
        { key: 'planerade', label: 'Planerade', count: plannedColumn.length },
        { key: 'pagaende', label: 'Pågående', count: inProgressColumn.length },
        { key: 'utforda', label: 'Utförda', count: completedColumn.length },
    ];

    const breadcrumbItems = routeBuildingKey
        ? [
            { text: routeBuilding?.name ?? 'Byggnad', link: `/building/${routeBuildingKey}` },
            { text: 'Projekt' }
        ]
        : [{ text: 'Projekt' }];

    return (
        <div className="management-page project-board">
            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent items={breadcrumbItems} />
            </div>
            <div className="management-page__header">
                <h1 className="management-page__title page-title">Projekt</h1>
                <div className="management-page__search">
                    <div className="management-page__search-input">
                        <Search size={14} className="management-page__search-icon" />
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Sök..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                className="management-page__search-clear"
                                onClick={() => setSearchQuery('')}
                                aria-label="Rensa sökning"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>
                </div>
                <div className="d-flex gap-2 management-page__actions">
                    {!routeBuildingKey && (
                        <Dropdown autoClose="outside">
                            <Dropdown.Toggle
                                variant={buildingFilterActive ? 'primary' : 'outline-secondary'}
                                className="d-flex align-items-center gap-2"
                                aria-label="Filter"
                            >
                                <Filter size={18} aria-hidden="true" />
                                <span className="management-filter-label">Filter</span>
                                <span className={`badge building-type-filter-badge ms-1${buildingFilterActive ? ' building-type-filter-badge--active' : ''}`}>
                                    {selectedFilterCount}/{totalFilterCount}
                                </span>
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="building-type-filter-menu building-filter-menu">
                                <div className="building-filter-columns">
                                    <FilterCheckboxColumn
                                        title="Byggnadstyp"
                                        options={ALL_BUILDING_TYPES.map(type => ({ key: type, label: translate(type) }))}
                                        isSelected={(key) => selectedBuildingTypes.includes(key as BuildingType)}
                                        allSelected={selectedBuildingTypes.length === ALL_BUILDING_TYPES.length}
                                        onToggle={(key) => toggleBuildingType(key as BuildingType)}
                                        onToggleAll={toggleAllBuildingTypes}
                                    />
                                    {tagColumnVisible && (
                                        <FilterCheckboxColumn
                                            title="Byggnadsgrupp"
                                            options={[
                                                { key: UNTAGGED_OPTION_VALUE, label: 'Utan grupp' },
                                                ...availableTags.map(tag => ({ key: tag.key, label: tag.tag }))
                                            ]}
                                            isSelected={isTagOptionSelected}
                                            allSelected={allTagOptionsSelected}
                                            onToggle={toggleTagOption}
                                            onToggleAll={toggleAllTagOptions}
                                        />
                                    )}
                                    {buildingColumnVisible && (
                                        <FilterCheckboxColumn
                                            title="Byggnad"
                                            options={buildingOptions}
                                            isSelected={isBuildingSelected}
                                            allSelected={allBuildingsSelected}
                                            onToggle={toggleBuilding}
                                            onToggleAll={toggleAllBuildings}
                                            searchPlaceholder="Sök byggnad…"
                                        />
                                    )}
                                </div>
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                    <Button
                        variant="primary"
                        onClick={() => setShowNewProject(true)}
                        className="d-flex align-items-center gap-2"
                    >
                        <Plus size={18} />
                        Nytt projekt
                    </Button>
                </div>
            </div>
            {isSmallScreen && (
                <div className="management-zone-tabs" role="tablist" aria-label="Visa del av tavlan">
                    {mobileZones.map(zone => (
                        <button
                            key={zone.key}
                            type="button"
                            role="tab"
                            aria-selected={mobileZone === zone.key}
                            className={`management-zone-tab${mobileZone === zone.key ? ' active' : ''}`}
                            onClick={() => setMobileZone(zone.key)}
                        >
                            <span>{zone.label}</span>
                            <span className="management-zone-tab__count">{zone.count}</span>
                        </button>
                    ))}
                </div>
            )}
            <div className="management-page__container" data-mobile-zone={mobileZone}>
                <div className="management-columns">
                    <div className="management-phase-group project-board__group project-board__group--planerade">
                        <div className="management-column-wrapper">
                            <ColumnCard
                                title="Planerade"
                                icon={<CalendarClock size={20} />}
                                count={plannedColumn.length}
                                loading={loading}
                                emptyMessage="Inga planerade projekt"
                            >
                                {renderColumn('planerade', plannedColumn)}
                            </ColumnCard>
                        </div>
                    </div>

                    <div className="management-phase-group project-board__group project-board__group--pagaende">
                        <div className="management-column-wrapper">
                            <ColumnCard
                                title="Pågående"
                                icon={<FolderKanban size={20} />}
                                count={inProgressColumn.length}
                                loading={loading}
                                emptyMessage="Inga pågående projekt"
                                titleInfo="Projekt med förfallna delmoment eller passerat slutdatum visas först och öppnas automatiskt."
                            >
                                {renderColumn('pagaende', inProgressColumn)}
                            </ColumnCard>
                        </div>
                    </div>

                    <div className="management-phase-group project-board__group project-board__group--utforda">
                        <div className="management-column-wrapper">
                            <ColumnCard
                                title={showAllCompleted ? 'Utförda' : `Utförda ${currentYear}`}
                                icon={<CheckCircle size={20} />}
                                count={completedColumn.length}
                                loading={loading}
                                emptyMessage={showAllCompleted ? 'Inga utförda projekt' : `Inga utförda projekt ${currentYear}`}
                                footer={!showAllCompleted && completedAll.length > completedColumn.length && (
                                    <div className="text-center py-2">
                                        <button
                                            type="button"
                                            onClick={() => setShowAllCompleted(true)}
                                            className="btn btn-link text-primary p-0"
                                            style={{ textDecoration: 'none' }}
                                        >
                                            Visa alla ({completedAll.length})
                                        </button>
                                    </div>
                                )}
                            >
                                {renderColumn('utforda', completedColumn)}
                            </ColumnCard>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delmoment detail */}
            {viewingWorkOrderKey && (
                <WorkOrderDetailModal
                    show={true}
                    onHide={closeWorkOrder}
                    workOrderKey={viewingWorkOrderKey}
                    onCompleted={() => { closeWorkOrder(); refreshBoard(); }}
                    onCancelled={() => { closeWorkOrder(); refreshBoard(); }}
                    onDeleted={() => { closeWorkOrder(); refreshBoard(); }}
                    onReissued={() => { closeWorkOrder(); refreshBoard(); }}
                    onChanged={refreshDelmoment}
                    getUserName={getUserName}
                    onExternalUserCreated={refreshUsers}
                />
            )}

            {/* Quick actions */}
            {assigningWorkOrder && (
                <AssignWorkOrderModal
                    show={true}
                    onHide={() => setAssigningWorkOrderKey(null)}
                    workOrder={assigningWorkOrder}
                    internalUsers={activeUsers}
                    externalUsers={externalUserList}
                    onAssigned={refreshDelmoment}
                    onExternalUserCreated={refreshUsers}
                />
            )}
            {dueDateTask && (
                <DueDateModal
                    show={true}
                    onHide={() => setDueDateTask(null)}
                    task={dueDateTask}
                    onConfirm={handleDueDateConfirm}
                />
            )}

            {/* Create / link delmoment */}
            {creatingForProject && (
                <CreateWorkOrderModal
                    show={true}
                    onHide={() => setCreatingForProject(null)}
                    project={{ key: creatingForProject.key, buildingKey: creatingForProject.buildingKey }}
                    onSuccess={() => { setCreatingForProject(null); refreshDelmoment(); }}
                    onExternalUserCreated={refreshUsers}
                />
            )}
            {linkingForProject && (
                <LinkWorkOrdersModal
                    show={true}
                    onHide={() => setLinkingForProject(null)}
                    unlinkedWorkOrders={unlinkedWorkOrders}
                    onLink={handleLink}
                    getUserName={getUserName}
                />
            )}

            {/* New project */}
            <NewProjectModal
                show={showNewProject}
                onHide={() => setShowNewProject(false)}
                buildingKey={routeBuildingKey}
                buildings={buildings?.filter(b => matchesBuildingFilter(b.key))}
                onCreated={handleProjectCreated}
            />
        </div>
    );
};

export default ProjectBoardPage;
